import { useState } from 'react';
import { Button, Input } from '@react-apps/design-system';
import type { TotvsEnrollmentPayload } from '../../domain/entities/totvs.entity';
import { useUpdateTotvsSyncTaskPayload } from '../hooks/use-totvs-sync-tasks';
import { ErrorText, getApiErrorMessage } from '../display';

const EMPTY_PAYLOAD: TotvsEnrollmentPayload = {
  codColigada: 1,
  idPs: '',
  idAreaInteresse: '',
  cpf: '',
  nome: '',
  dtNascimento: '',
  email: '',
  telefone: '',
  cep: '',
  endereco: '',
  numero: '',
  complemento: null,
  bairro: '',
  cidade: '',
  estado: '',
  pais: '',
};

interface TotvsPayloadEditFormProps {
  taskId: string;
  payload: TotvsEnrollmentPayload | null;
  onDone: () => void;
}

/**
 * Formulário de edição do snapshot ZMDSALUNO (§9.4) — corrige dados ruins
 * (ex.: CNPJ salvo onde só cabe CPF) direto pelo admin, sem cirurgia no
 * banco. Não reprocessa sozinho: o admin salva e depois clica em
 * "Reprocessar agora" no card acima.
 */
export function TotvsPayloadEditForm({
  taskId,
  payload,
  onDone,
}: TotvsPayloadEditFormProps) {
  const base = payload ?? EMPTY_PAYLOAD;
  const [form, setForm] = useState({
    codColigada: String(base.codColigada),
    idPs: base.idPs,
    idAreaInteresse: base.idAreaInteresse,
    cpf: base.cpf,
    nome: base.nome,
    dtNascimento: base.dtNascimento,
    email: base.email,
    telefone: base.telefone,
    cep: base.cep,
    endereco: base.endereco,
    numero: base.numero,
    complemento: base.complemento ?? '',
    bairro: base.bairro,
    cidade: base.cidade,
    estado: base.estado,
    pais: base.pais,
  });
  const [localError, setLocalError] = useState<string | null>(null);
  const updatePayload = useUpdateTotvsSyncTaskPayload();

  const setField =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const parsedCodColigada = Number(form.codColigada);
    if (!Number.isInteger(parsedCodColigada) || parsedCodColigada < 1) {
      setLocalError('CODCOLIGADA deve ser um inteiro positivo.');
      return;
    }

    const newPayload: TotvsEnrollmentPayload = {
      codColigada: parsedCodColigada,
      idPs: form.idPs.trim(),
      idAreaInteresse: form.idAreaInteresse.trim(),
      cpf: form.cpf.trim(),
      nome: form.nome.trim(),
      dtNascimento: form.dtNascimento.trim(),
      email: form.email.trim(),
      telefone: form.telefone.trim(),
      cep: form.cep.trim(),
      endereco: form.endereco.trim(),
      numero: form.numero.trim(),
      complemento: form.complemento.trim() || null,
      bairro: form.bairro.trim(),
      cidade: form.cidade.trim(),
      estado: form.estado.trim(),
      pais: form.pais.trim(),
    };

    updatePayload.mutate(
      {
        id: taskId,
        input: {
          payload: newPayload,
          reason: 'Payload editado manualmente pelo admin',
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <Input
          label="CODCOLIGADA"
          type="number"
          min="1"
          value={form.codColigada}
          onChange={setField('codColigada')}
          required
        />
        <Input label="IDPS" value={form.idPs} onChange={setField('idPs')} />
        <Input
          label="IDAREAINTERESSE"
          value={form.idAreaInteresse}
          onChange={setField('idAreaInteresse')}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="CPF"
          value={form.cpf}
          onChange={setField('cpf')}
          helperText="Só dígitos — 11 posições."
        />
        <Input label="NOME" value={form.nome} onChange={setField('nome')} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="DTNASCIMENTO"
          value={form.dtNascimento}
          onChange={setField('dtNascimento')}
        />
        <Input label="EMAIL" value={form.email} onChange={setField('email')} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="TELEFONE"
          value={form.telefone}
          onChange={setField('telefone')}
        />
        <Input label="CEP" value={form.cep} onChange={setField('cep')} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Input
          label="ENDERECO"
          value={form.endereco}
          onChange={setField('endereco')}
        />
        <Input
          label="NUMERO"
          value={form.numero}
          onChange={setField('numero')}
        />
        <Input
          label="COMPLEMENTO"
          value={form.complemento}
          onChange={setField('complemento')}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="BAIRRO"
          value={form.bairro}
          onChange={setField('bairro')}
        />
        <Input
          label="CIDADE"
          value={form.cidade}
          onChange={setField('cidade')}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="ESTADO"
          value={form.estado}
          onChange={setField('estado')}
        />
        <Input label="PAIS" value={form.pais} onChange={setField('pais')} />
      </div>

      {(localError || updatePayload.error) && (
        <ErrorText>
          {localError ??
            getApiErrorMessage(updatePayload.error, 'Falha ao salvar payload')}
        </ErrorText>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" disabled={updatePayload.isPending}>
          {updatePayload.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
